import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, PenLine, LogOut, User as UserIcon, ChevronDown, Sparkles, Radio, Tv, BookOpen, ArrowLeft } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { SECTIONS, SECTION_MAP } from "@/lib/sections";
import { NotificationBell } from "./NotificationBell";
import { SearchDrawer } from "./SearchDrawer";
import { api } from "@/lib/api";
import { playChime } from "@/lib/chime";

export const Navbar = () => {
  // ────────────────────────────────────────────────────────────────
  //  🛡️ PROTECTED LAYOUT — MOBILE TOP-RIGHT ICON CLUSTER
  //  Order (mobile, right side):
  //    POST pill · Notebook (Editor's Letter, mobile-only) · TV · Search · Bell · Avatar
  //  ONLY the notebook icon is mobile-only (sm:hidden) because desktop already
  //  has an "Editor's Letter" text link in the main nav. TV stays on both.
  //  Do NOT silently remove OR swap any of these icons in this cluster —
  //  Mimi has explicitly asked for both TV and the notebook to coexist on mobile.
  //  History: Batch 5b (commit 7315fcf) mistakenly replaced the notebook with
  //  the TV icon; restored on 2026-02-16. See PRD "Absolute no-go's".
  // ────────────────────────────────────────────────────────────────
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [roomsOpen, setRoomsOpen] = useState(false);
  const roomsCloseTimerRef = useRef(null);
  const openRooms = () => {
    if (roomsCloseTimerRef.current) {
      clearTimeout(roomsCloseTimerRef.current);
      roomsCloseTimerRef.current = null;
    }
    setRoomsOpen(true);
  };
  const scheduleCloseRooms = () => {
    if (roomsCloseTimerRef.current) clearTimeout(roomsCloseTimerRef.current);
    roomsCloseTimerRef.current = setTimeout(() => setRoomsOpen(false), 180);
  };
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [openReportsCount, setOpenReportsCount] = useState(0);
  const [liveActive, setLiveActive] = useState(false);
  const prevUnreadMessagesRef = useRef(null);
  const prevMentionCountRef = useRef(null);

  useEffect(() => {
    if (!user) {
      prevUnreadMessagesRef.current = null;
      prevMentionCountRef.current = null;
      return;
    }
    let active = true;

    const refresh = async () => {
      try {
        const [msgRes, notifRes] = await Promise.all([
          api.get("/conversations/unread-count"),
          api.get("/notifications/unread-count"),
        ]);
        if (!active) return;
        const newMsgCount = msgRes.data?.count || 0;
        const newMentionCount = notifRes.data?.mention_count || 0;
        // Chime rules (Mimi's option C, extended for mentions):
        //   - new DM AND user is NOT on /messages → chime
        //   - new @mention AND user is NOT on the related post → chime
        const onMessages = location.pathname.startsWith("/messages");
        if (
          prevUnreadMessagesRef.current !== null &&
          newMsgCount > prevUnreadMessagesRef.current &&
          !onMessages
        ) {
          playChime();
        }
        if (
          prevMentionCountRef.current !== null &&
          newMentionCount > prevMentionCountRef.current
        ) {
          playChime();
        }
        prevUnreadMessagesRef.current = newMsgCount;
        prevMentionCountRef.current = newMentionCount;
        setUnreadMessages(newMsgCount);
      } catch {
        // silent — polling
      }
    };
    refresh();
    const t = setInterval(refresh, 15000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  // Poll open reports count (editor only) every 30s + on window focus
  useEffect(() => {
    if (!user?.is_editor) {
      setOpenReportsCount(0);
      return;
    }
    let active = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/reports/counts");
        if (active) setOpenReportsCount(data?.open || 0);
      } catch {
        // silent
      }
    };
    refresh();
    const t = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { active = false; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user]);

  // Poll live broadcast status every 30s so the TV icon can pulse red when a broadcast is active
  useEffect(() => {
    if (!user) { setLiveActive(false); return; }
    let active = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/live/status");
        if (active) setLiveActive(!!data?.active);
      } catch {
        // silent — Mux may not be configured
      }
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  const isActive = (p) => location.pathname === p;
  const isRoomActive = location.pathname.startsWith("/room") || location.pathname.startsWith("/rubrique");

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backdropFilter: "blur(20px)",
        backgroundColor: "rgba(250, 249, 245, 0.92)",
        borderBottom: "1px solid var(--border-default)",
      }}
    >
      <div className="container-belle flex items-center justify-between" style={{ height: 76 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          {/* Universal back arrow — sends the user to their previous page.
              Falls back to /rooms (authed) or / (guest) on first-load tabs.
              Landing/Login/Register don't render <Navbar />, so no risk of
              showing this button where it shouldn't appear. */}
          <button
            type="button"
            data-testid="nav-back-button"
            aria-label="Back"
            title="Back"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate(user ? "/rooms" : "/");
            }}
            style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "transparent",
              border: "1px solid var(--border-default)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-primary)", cursor: "pointer",
              transition: "background 0.2s ease, border-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-alt)";
              e.currentTarget.style.borderColor = "var(--brand-accent)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "var(--border-default)";
            }}
          >
            <ArrowLeft size={16} strokeWidth={1.6} />
          </button>
          <Link to={user ? "/rooms" : "/"} data-testid="logo-link" className="flex items-center" style={{ gap: 10 }}>
            <img
              src="/brand/le-salon-door.png"
              alt=""
              aria-hidden="true"
              style={{ height: 34, width: 34, display: "block", borderRadius: 2 }}
            />
            <img
              src="/brand/le-salon-wordmark.png"
              alt="Le Salon"
              style={{ height: 40, width: "auto", display: "block" }}
            />
          </Link>
        </div>

        <nav className="hidden md:flex items-center" style={{ gap: 26 }}>
          {user && (
            <>
              <Link data-testid="nav-feed" to="/feed" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500,
                  color: isActive("/feed") ? "var(--brand-accent)" : "var(--text-primary)" }}>Salon</Link>

              <div style={{ position: "relative" }}
                onMouseEnter={openRooms}
                onMouseLeave={scheduleCloseRooms}>
                <button
                  data-testid="nav-rooms"
                  onClick={() => { setRoomsOpen(false); navigate("/rooms"); }}
                  style={{
                    background: "transparent", border: "none", cursor: "pointer", padding: 0,
                    fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500,
                    color: isRoomActive ? "var(--brand-accent)" : "var(--text-primary)",
                    display: "flex", alignItems: "center", gap: 4, fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  Rooms <ChevronDown size={12} strokeWidth={1.6} />
                </button>
                {roomsOpen && (
                  <div data-testid="rooms-dropdown"
                    onMouseEnter={openRooms}
                    onMouseLeave={scheduleCloseRooms}
                    style={{
                      position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                      paddingTop: 12,  // hover-bridge: keeps mouse inside the container
                      zIndex: 100,
                    }}>
                    <div style={{
                      background: "var(--surface)", border: "1px solid var(--border-default)",
                      minWidth: 240, padding: "10px 0",
                      boxShadow: "0 12px 40px rgba(51,51,51,0.08)",
                    }}>
                    {SECTIONS.map((s) => (
                      <Link key={s.slug} to={`/room/${s.slug}`}
                        data-testid={`dropdown-section-${s.slug}`}
                        onClick={() => setRoomsOpen(false)}
                        style={{
                          display: "block", padding: "10px 20px",
                          fontFamily: "'Cormorant Garamond', serif", fontSize: 18,
                          color: "var(--text-primary)",
                          borderLeft: "2px solid transparent", transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-alt)"; e.currentTarget.style.borderLeftColor = s.accent; e.currentTarget.style.color = s.accent; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderLeftColor = "transparent"; e.currentTarget.style.color = "var(--text-primary)"; }}>
                        {s.label}
                      </Link>
                    ))}
                    <div style={{ borderTop: "1px solid var(--border-default)", marginTop: 8, paddingTop: 8 }}>
                      <Link to="/rooms" onClick={() => setRoomsOpen(false)} data-testid="dropdown-all-rooms"
                        style={{ display: "block", padding: "10px 20px", fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--brand-accent)", fontWeight: 500 }}>
                        See all rooms →
                      </Link>
                    </div>
                    </div>
                  </div>
                )}
              </div>

              <Link data-testid="nav-journal" to="/journal" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500,
                  color: isActive("/journal") ? "var(--brand-accent)" : "var(--text-primary)" }}>Letters</Link>
              <Link data-testid="nav-private-notes" to="/messages" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500, position: "relative",
                  display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.05, textAlign: "center",
                  color: isActive("/messages") ? "var(--brand-accent)" : "var(--text-primary)" }}>
                <span style={{ display: "block" }}>Private</span>
                <span style={{ display: "block" }}>Notes</span>
                {unreadMessages > 0 && (
                  <span
                    data-testid="private-notes-unread-badge"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -14,
                      minWidth: 16,
                      height: 16,
                      padding: "0 4px",
                      borderRadius: 8,
                      background: "var(--brand-accent)",
                      color: "#FAF9F5",
                      fontSize: 9,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "'Outfit', sans-serif",
                      letterSpacing: 0,
                    }}
                  >
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </Link>
              <Link data-testid="nav-editors-letter" to="/editors-letter" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500,
                  display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.05, textAlign: "center",
                  color: isActive("/editors-letter") ? "var(--brand-accent)" : "var(--text-primary)" }}>
                <span style={{ display: "block" }}>Editor&apos;s</span>
                <span style={{ display: "block" }}>Journal</span>
              </Link>
              {user.is_editor && (
                <Link data-testid="nav-live" to="/live" className="link-underline"
                  style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500,
                    color: isActive("/live") ? "var(--brand-accent)" : "var(--text-primary)" }}>Broadcasts</Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center" style={{ gap: 16 }}>
          {user ? (
            <>
              <button
                type="button"
                data-testid="nav-compose"
                title="New post"
                aria-label="New post"
                onClick={() => {
                  try {
                    const raw = localStorage.getItem("salon.compose.recent_rooms");
                    const arr = raw ? JSON.parse(raw) : [];
                    const first = Array.isArray(arr) ? arr[0] : null;
                    if (first && SECTION_MAP[first]) {
                      navigate(`/room/${first}`);
                      return;
                    }
                  } catch { /* fall through */ }
                  navigate("/rooms");
                }}
                className="hidden sm:inline-flex sm:ml-[18px]"
                style={{
                  alignItems: "center",
                  gap: 6,
                  background: "var(--brand-accent)",
                  color: "#FAF9F5",
                  border: "1px solid var(--brand-accent)",
                  padding: "5px 9px",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "opacity 0.18s ease, transform 0.18s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <PenLine size={14} strokeWidth={1.8} />
                <span className="hidden sm:inline">Post</span>
              </button>
              <Link to="/live" data-testid="nav-broadcasts-icon" title={liveActive ? "Live now — tap to watch" : "Broadcasts"} style={{ position: "relative", display: "inline-block" }}>
                <Tv size={20} strokeWidth={1.4} color="var(--text-primary)" />
                {liveActive && (
                  <span
                    data-testid="live-active-dot"
                    aria-label="Live broadcast in progress"
                    style={{
                      position: "absolute",
                      top: -2, right: -3,
                      width: 9, height: 9, borderRadius: "50%",
                      background: "#D33F3F",
                      border: "2px solid var(--surface, #FAF9F5)",
                      boxShadow: "0 0 0 0 rgba(211, 63, 63, 0.7)",
                      animation: "salonLivePulse 1.6s ease-out infinite",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </Link>
              {/* 🛡️ PROTECTED — mobile-only Editor's Letter notebook icon.
                  Desktop already has an "Editor's Letter" text link in the main nav,
                  so this icon is hidden on md+ to avoid duplication.
                  Do NOT remove or swap this icon. See Navbar top-of-component note. */}
              <Link
                to="/editors-letter"
                data-testid="nav-editors-letter-icon"
                title="Editor's Journal"
                aria-label="Editor's Journal"
                className="sm:hidden"
                style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", height: 24, width: 24, lineHeight: 0 }}
              >
                <BookOpen size={20} strokeWidth={1.4} color="var(--text-primary)" />
              </Link>
              <SearchDrawer />
              <NotificationBell />
              <div className="relative">
                <button data-testid="nav-profile-button" onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center" style={{ gap: 8, position: "relative" }}>
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.username}
                      style={{ width: 36, height: 36, objectFit: "cover", borderRadius: "50%", border: "1px solid var(--border-default)" }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bg-alt)", display: "grid", placeItems: "center", border: "1px solid var(--border-default)" }}>
                      <UserIcon size={18} strokeWidth={1.3} />
                    </div>
                  )}
                  {user.is_editor && openReportsCount > 0 && (
                    <span
                      data-testid="avatar-reports-dot"
                      title={`${openReportsCount} open report${openReportsCount === 1 ? "" : "s"}`}
                      style={{
                        position: "absolute",
                        bottom: -1, right: 8,
                        width: 10, height: 10, borderRadius: "50%",
                        background: "var(--brand-accent)",
                        border: "2px solid var(--surface)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </button>
                {menuOpen && (
                  <div data-testid="profile-dropdown"
                    onMouseLeave={() => setMenuOpen(false)}
                    style={{ position: "absolute", top: 48, right: 0, background: "var(--surface)", border: "1px solid var(--border-default)", minWidth: 240, padding: "8px 0", zIndex: 100, boxShadow: "0 12px 40px rgba(51,51,51,0.08)" }}>
                    <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border-default)" }}>
                      <div className="font-serif" style={{ fontSize: 18 }}>{user.display_name}</div>
                      <div className="overline" style={{ marginTop: 2 }}>@{user.username}</div>
                    </div>
                    <Link to={`/profile/${user.username}`} data-testid="menu-my-profile" onClick={() => setMenuOpen(false)}
                      style={{ display: "block", padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em" }}>My Profile</Link>
                    <Link to="/settings" data-testid="menu-settings" onClick={() => setMenuOpen(false)}
                      style={{ display: "block", padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em" }}>Edit Profile</Link>
                    <Link to="/settings#notifications" data-testid="menu-settings-prefs" onClick={() => setMenuOpen(false)}
                      style={{ display: "block", padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em" }}>Settings &amp; Notifications</Link>
                    <Link to="/rooms" data-testid="menu-favorite-rooms" onClick={() => setMenuOpen(false)}
                      style={{ display: "block", padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em" }}>Your Favorite Rooms</Link>
                    {user.is_editor && (
                      <Link to="/live" data-testid="menu-live" onClick={() => setMenuOpen(false)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em" }}>
                        <Radio size={14} strokeWidth={1.4} />
                        Live Studio
                      </Link>
                    )}
                    {user.is_editor && (
                      <Link to="/founder" data-testid="menu-founder-desk" onClick={() => setMenuOpen(false)}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em", borderTop: "1px solid var(--border-default)", color: "var(--brand-accent)", fontWeight: 500 }}>
                        <Sparkles size={14} strokeWidth={1.4} />
                        Founder&apos;s Desk
                        {openReportsCount > 0 && (
                          <span
                            data-testid="founder-reports-badge"
                            title={`${openReportsCount} open report${openReportsCount === 1 ? "" : "s"}`}
                            style={{
                              marginLeft: "auto",
                              minWidth: 20, height: 20, borderRadius: 10,
                              background: "var(--brand-accent)", color: "var(--text-inverse)",
                              fontSize: 10, fontWeight: 600, letterSpacing: 0,
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              padding: "0 6px",
                            }}
                          >
                            {openReportsCount}
                          </span>
                        )}
                      </Link>
                    )}
                    <button data-testid="menu-logout"
                      onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                      style={{ width: "100%", textAlign: "left", padding: "12px 18px", fontSize: 13, letterSpacing: "0.05em", background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "var(--brand-accent)" }}>
                      <LogOut size={14} /> Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"
                style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 500 }}>Sign in</Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary" style={{ textDecoration: "none" }}>
                Join the salon
              </Link>
            </>
          )}
        </div>
      </div>
      <style>{`
        @keyframes salonLivePulse {
          0%   { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0.65); }
          70%  { box-shadow: 0 0 0 8px rgba(211, 63, 63, 0); }
          100% { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0); }
        }
        /* Desktop-safety: also protect the avatar from flex-shrink at all
           viewports (not just mobile) — the 1280px max-width container is
           tight when Mimi has many nav links + right-cluster icons. */
        @media (min-width: 768px) {
          [data-testid="navbar"] .container-belle {
            padding-left: 2rem !important;
            padding-right: 2rem !important;
          }
          [data-testid="navbar"] [data-testid="nav-profile-button"] {
            flex-shrink: 0 !important;
            min-width: 36px !important;
          }
          [data-testid="navbar"] [data-testid="nav-profile-button"] img,
          [data-testid="navbar"] [data-testid="nav-profile-button"] > div {
            flex-shrink: 0 !important;
          }
          [data-testid="navbar"] .container-belle > div:last-child > div.relative {
            flex-shrink: 0 !important;
          }
        }
        /* Mobile-only: give the "Le Salon" wordmark a touch of breathing room
           from the right-side icon cluster, and tighten the icon gap so
           everything fits comfortably on a 390px viewport. Wordmark bumped
           to 44px so it visually matches the door icon top/bottom line and
           feels bold on the phone. */
        @media (max-width: 767px) {
          [data-testid="navbar"] .container-belle {
            padding-left: 1rem !important;
            padding-right: 0.75rem !important;
          }
          [data-testid="navbar"] [data-testid="logo-link"] {
            gap: 8px !important;
          }
          [data-testid="navbar"] [data-testid="logo-link"] img[alt=""] {
            height: 40px !important;
            width: 40px !important;
          }
          [data-testid="navbar"] [data-testid="logo-link"] img[alt="Le Salon"] {
            height: auto !important;
            max-height: 40px !important;
            max-width: 148px !important;
            width: auto !important;
          }
          /* Prevent flex-shrink from squishing the avatar button on mobile. */
          [data-testid="navbar"] [data-testid="nav-profile-button"] {
            flex-shrink: 0 !important;
            min-width: 36px !important;
          }
          [data-testid="navbar"] [data-testid="nav-profile-button"] img,
          [data-testid="navbar"] [data-testid="nav-profile-button"] > div {
            flex-shrink: 0 !important;
          }
          /* Also protect the avatar wrapper against shrink from the outer flex. */
          [data-testid="navbar"] .container-belle > div:last-child > div.relative {
            flex-shrink: 0 !important;
          }
          [data-testid="navbar"] .container-belle > div:last-child {
            gap: 14px !important;
          }
        }
      `}</style>
    </header>
  );
};
